// Google Places (New) address suggestions for form address fields. The Maps script loads on first
// use with VUE_APP_GOOGLE_MAPS_KEY (a browser key restricted to Places API (New) + Maps
// JavaScript API); without a key, or if Google rejects it, address fields stay plain text.
// Mirrors moxie-app src/modules/maps/googlePlaces.js; keep the address parsing in sync.
const KEY = process.env.VUE_APP_GOOGLE_MAPS_KEY;
const CALLBACK = '__moxieGooglePlacesReady';

let placesLibrary = null;

export function placesAvailable() {
	return Boolean(KEY);
}

export function loadPlaces() {
	if (!KEY) return Promise.reject(new Error('Google Maps is not configured.'));
	if (!placesLibrary) {
		placesLibrary = new Promise((resolve, reject) => {
			window[CALLBACK] = () => {
				delete window[CALLBACK];
				resolve();
			};
			const script = document.createElement('script');
			script.id = 'moxie-google-places';
			script.async = true;
			script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(KEY)}&v=weekly&loading=async&callback=${CALLBACK}`;
			script.onerror = () => {
				script.remove();
				placesLibrary = null;
				reject(new Error('Google Maps could not be loaded.'));
			};
			document.head.appendChild(script);
		}).then(() => window.google.maps.importLibrary('places'));
	}
	return placesLibrary;
}

// One token covers the keystrokes and the final place lookup of a single address entry, so Google
// bills it as one session. Start a new one after each selection.
export async function newSessionToken() {
	const { AutocompleteSessionToken } = await loadPlaces();
	return new AutocompleteSessionToken();
}

const text = (value) => (value && value.text) || '';

export async function fetchSuggestions(input, sessionToken) {
	const { AutocompleteSuggestion } = await loadPlaces();
	const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({ input, sessionToken });
	return suggestions
		.map((suggestion) => suggestion.placePrediction)
		.filter(Boolean)
		.map((prediction) => ({
			id: prediction.placeId,
			text: text(prediction.text),
			mainText: text(prediction.mainText) || text(prediction.text),
			secondaryText: text(prediction.secondaryText),
			prediction,
		}));
}

export async function fetchPlaceAddress(suggestion) {
	const place = suggestion.prediction.toPlace();
	await place.fetchFields({ fields: ['addressComponents', 'formattedAddress'] });
	return {
		placeId: place.id || suggestion.id,
		formattedAddress: place.formattedAddress || suggestion.text,
		...parseAddressComponents(place.addressComponents || []),
	};
}

// Countries that write the house number after the street name ("Hauptstraße 5").
const NUMBER_AFTER_STREET = [
	'AR', 'AT', 'BE', 'BR', 'CH', 'CL', 'CO', 'CZ', 'DE', 'DK', 'ES', 'FI', 'GR', 'HR', 'HU', 'IL', 'IT', 'MX', 'NL', 'NO',
	'PL', 'PT', 'RO', 'RU', 'SE', 'SI', 'SK', 'TR', 'UA',
];
// Countries whose state/province is conventionally abbreviated in an address ("CA", "ON", "NSW").
const ABBREVIATED_REGIONS = ['US', 'CA', 'AU'];

// Maps Google address components onto the Moxie address fields (address1, address2, city,
// locality = state/province, postal, country = ISO code). address2 is only returned when Google
// knows a unit, so a hand-entered suite is not cleared.
export function parseAddressComponents(components) {
	const component = (type) => components.find((item) => item.types && item.types.includes(type));
	const long = (type) => (component(type) || {}).longText || '';
	const short = (type) => (component(type) || {}).shortText || '';
	const country = short('country');
	const number = long('street_number');
	const street = long('route');
	const parts = NUMBER_AFTER_STREET.includes(country) ? [street, number] : [number, street];
	const address = {
		address1: parts.filter(Boolean).join(' ') || long('premise'),
		city:
			long('locality') ||
			long('postal_town') ||
			long('sublocality_level_1') ||
			long('sublocality') ||
			long('administrative_area_level_3') ||
			long('administrative_area_level_2'),
		locality: ABBREVIATED_REGIONS.includes(country) ? short('administrative_area_level_1') : long('administrative_area_level_1'),
		postal: long('postal_code'),
		country,
	};
	const unit = long('subpremise');
	if (unit) address.address2 = unit;
	return address;
}

// Form v2 address fields are separate items bound by their schemaMapping. The item mapped to
// address1 announces a chosen address for its form (identified by the form's shared results
// array), and every item in that form fills itself from the field its mapping names.
const ADDRESS_MAPPINGS = ['address1', 'address2', 'city', 'locality', 'postal', 'country'];
const listeners = new Set();

export function isAddressMapping(mapping) {
	return ADDRESS_MAPPINGS.includes(mapping);
}

export function onAddressSelected(listener) {
	listeners.add(listener);
	return () => listeners.delete(listener);
}

export function announceAddress(form, address) {
	listeners.forEach((listener) => listener(form, address));
}
